import { useEffect, useState } from "react";
import {
  Landmark, Plus, Pencil, Trash2, X, Users,
  ChevronDown, ChevronRight, AlertTriangle, Check, ChevronsUpDown
} from "lucide-react";
import "../css/Banks.css";
import axios from 'axios';

const API_URL = "/api/banks";
const STATES_API_URL = "/api/states";
const VEHICLES_API_URL = "/api/vehicles";

const CLOUDINARY_CLOUD = "dnriybfwk";       // ← replace
const CLOUDINARY_PRESET = "ofak_assets"; 

const CIBIL_CATEGORIES = [
  { value: "building",   label: "Building",  range: "< 500",    color: "red"    },
  { value: "good",       label: "Good",      range: "500-749",  color: "yellow" },
  { value: "excellent",  label: "Excellent", range: "750+",     color: "green"  },
];

const getContentType = (file) => {
  if (file.type) return file.type;
  if (file.name.endsWith(".glb")) return "model/gltf-binary";
  if (file.name.endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
};

const uploadToS3 = async (file) => {
  // 1. get a signed upload URL from backend
  const { data } = await axios.post(
    "/api/assets/upload-url",
    {
      fileName: file.name,
      contentType: getContentType(file),
    }
  );

  const { uploadUrl, fileUrl } = data.data;

  // 2. upload the file directly to S3 using the signed URL
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": getContentType(file),
    },
  });

  // 3. return the CDN/public URL to store in form state
  return fileUrl;
};


const uploadToCloudinary = async (file) => {
  const data = new FormData();
  data.append("file", file);
  data.append("upload_preset", CLOUDINARY_PRESET);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`,
    { method: "POST", body: data }
  );

  const result = await res.json();

  if (!res.ok || !result.secure_url) {
    throw new Error(result.error?.message || "Logo upload failed");
  }

  return result.secure_url;
};

const TENURES    = [12, 24, 36, 48, 60];
//const PRICE_BANDS = [">1.15 lacs", "<1.15 lacs"];

let _id = 1000;
const uid        = () => ++_id;
const inr        = (n) => n || n === 0 ? Number(n).toLocaleString("en-IN") : "—";
const getVehicle = (vehicles, id) => vehicles.find((v) => v.id === Number(id));
const getState   = (states, id) => states.find((s) => s.id === Number(id));
const normalizeBank = (bank) => ({
  ...bank,
  schemeNames: bank.schemeNames || [],
  customerTypes: bank.customerTypes || [],
  combinations: bank.combinations || [],
  rows: bank.rows || bank.BankSchemeRows || [],
   offers:          bank.offers          || [],
  cibilCategories: bank.cibilCategories || [],   // ← add
});
const pickOnRoadPrice = (vehicle) => {
  const values = vehicle.VehicleStatePrices?.[0]?.values || {};
  return (
    vehicle.onRoadPrice ??
    vehicle.on_road_price ??
    vehicle.price ??
    values["On Road Price"] ??
    values["onRoadPrice"] ??
    values["on_road_price"] ??
    ""
  );
};
const normalizeVehicle = (vehicle) => ({
  id: vehicle.id,
  model: vehicle.variantName || vehicle.vehicleName || vehicle.name || `Vehicle #${vehicle.id}`,
  onRoadPrice: pickOnRoadPrice(vehicle),
  VehicleStatePrices: vehicle.VehicleStatePrices || [],
});

const EMPTY_FORM = () => ({
  schemeName: "", customerType: "",
  stateId: "", vehicleId: "",
   tenure: "",
  interestPct: "", loanPct: "",
  // loanAmount: "",
  //emi: "", 
  pf: "0", docCharges: "", 
  //gst: "0", 
  //downPayment: "",

});

const calculatePMT = (rate, nper, pv) => {
  if (rate === 0) return pv / nper;

  return (
    (rate * pv * Math.pow(1 + rate, nper)) /
    (Math.pow(1 + rate, nper) - 1)
  );
};


function Sel({ value, onChange, options, placeholder, error }) {
  return (
    <div className={`bk-sel-wrap${error ? " bk-sel-err" : ""}`}>
      <select className="bk-sel" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) =>
          typeof o === "object"
            ? <option key={o.value} value={o.value}>{o.label}</option>
            : <option key={o} value={o}>{o}</option>
        )}
      </select>
      <ChevronsUpDown size={11} className="bk-sel-icon" />
    </div>
  );
}


function Confirm({ title, msg, onOk, onCancel }) {
  return (
    <div className="bk-overlay" onClick={onCancel}>
      <div className="bk-confirm" onClick={(e) => e.stopPropagation()}>
        <div className="bk-confirm-ico"><AlertTriangle size={22} /></div>
        <h3>{title}</h3>
        <p>{msg}</p>
        <div className="bk-confirm-btns">
          <button className="bk-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="bk-btn-danger" onClick={onOk}>Delete</button>
        </div>
      </div>
    </div>
  );
}


function BankModal({ bank, onSave, onClose }) {
  const isEdit = !!bank;
  const [name, setName]               = useState(bank?.name ?? "");
  const [nameErr, setNameErr]         = useState("");
  const [logoUrl]                     = useState(bank?.logoUrl ?? "");
  const [logoFile, setLogoFile]       = useState(null);
  const [saving, setSaving]           = useState(false);
  const [schemeNames, setSchemeNames] = useState(bank?.schemeNames?.length ? bank.schemeNames : [""]);
  const [customerTypes, setCT]        = useState(bank?.customerTypes?.length ? bank.customerTypes : [""]);

  const [offers, setOffers] = useState(bank?.offers?.length ? bank.offers : []);

  const [cibilCategories, setCibil] = useState(
  bank?.cibilCategories?.length ? bank.cibilCategories : []
);

  const EMPTY_COMBINATION = {
  id: uid(),
  schemeName: "",
  customerType: "",
  interestPct: "",
  loanPct: "",
  pfPct: "",
  flatRatePct: "",
  gstPct: "",
  pddCharges: "",
  docCharges: "",
};

const [combinations, setCombinations] = useState(
  bank?.combinations?.length
    ? bank.combinations
    : []
);

const toggleCibil = (val) =>
  setCibil((prev) =>
    prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
  );

  // scheme helpers
  const addSN    = () => setSchemeNames((a) => [...a, ""]);
  const removeSN = (i) => setSchemeNames((a) => a.filter((_, j) => j !== i));
  const updateSN = (i, v) => setSchemeNames((a) => a.map((x, j) => j === i ? v : x));

  // customer type helpers
  const addCT    = () => setCT((a) => [...a, ""]);
  const removeCT = (i) => setCT((a) => a.filter((_, j) => j !== i));
  const updateCT = (i, v) => setCT((a) => a.map((x, j) => j === i ? v : x));

  //offer helpers
  const addOffer = () =>
  setOffers((o) => [...o, ""]);

const removeOffer = (i) =>
  setOffers((o) => o.filter((_, idx) => idx !== i));

const updateOffer = (i, value) =>
  setOffers((o) =>
    o.map((offer, idx) =>
      idx === i ? value : offer
    )
  );

  const addCombination = () => {
  setCombinations((prev) => [
    ...prev,
    { ...EMPTY_COMBINATION, id: uid() },
  ]);
};

const removeCombination = (id) => {
  setCombinations((prev) =>
    prev.filter((c) => c.id !== id)
  );
};

const updateCombination = (id, field, value) => {
  setCombinations((prev) =>
    prev.map((c) =>
      c.id === id
        ? { ...c, [field]: value }
        : c
    )
  );
};

  const save = async () => {
    if (!name.trim()) { setNameErr("Bank name is required"); return; }

    setSaving(true);
    try {
      const finalLogoUrl = logoFile ? await uploadToS3(logoFile) : logoUrl;

    const isHdfc = name.toLowerCase().includes("hdfc");
    console.log("ishdfc", isHdfc);

const normalizedCombinations = combinations.map((c) => ({
  ...c,
  docCharges: isHdfc ? 0 : Number(c.docCharges || 0),
  pddCharges: isHdfc ? Number(c.pddCharges || 0) : 0,
}));

      await onSave({
        id: bank?.id ?? uid(),
        name: name.trim(),
        logoUrl: finalLogoUrl || null,
        schemeNames:   schemeNames.map((s) => s.trim()).filter(Boolean),
        customerTypes: customerTypes.map((c) => c.trim()).filter(Boolean),
        combinations: normalizedCombinations,
        offers: offers.map((o) => o.trim()).filter(Boolean),
        cibilCategories, 
        rows: bank?.rows ?? [],
      });
    } catch (err) {
      alert(err.message || "Failed to save bank");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bk-overlay" onClick={onClose}>
      <div className="bk-modal bk-modal-bank" onClick={(e) => e.stopPropagation()}>

        <div className="bk-modal-hd">
          <div className="bk-modal-hd-left">
            <Landmark size={16} />
            <span>{isEdit ? "Edit Bank" : "Add Bank"}</span>
          </div>
          <button className="bk-icon-btn" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="bk-modal-body">

          {/* Bank name */}
          <div className="bk-field">
            <label className="bk-lbl">Bank Name</label>
            <input
              className={`bk-input${nameErr ? " err" : ""}`}
              value={name} autoFocus placeholder="e.g. HDFC Bank"
              onChange={(e) => { setName(e.target.value); setNameErr(""); }}
            />
            {nameErr && <span className="bk-errtxt">{nameErr}</span>}
          </div>

          {/* Bank logo */}
          <div className="bk-field">
            <label className="bk-lbl">Bank Logo</label>
            {logoUrl && !logoFile && (
              <div className="bk-logo-preview">
                <img src={logoUrl} alt={name || "Bank logo"} />
                <span>Current logo</span>
              </div>
            )}
            <label className="bk-file-box">
              <input
                type="file"
                accept="image/*"
                className="bk-file-hidden"
                onChange={(e) => setLogoFile(e.target.files[0] || null)}
              />
              <span className="bk-file-text">
                {logoFile ? logoFile.name : logoUrl ? "Choose replacement logo..." : "Choose logo image..."}
              </span>
              <span className="bk-file-btn">Browse</span>
            </label>
          </div>

          {/* Scheme Names */}
          <div className="bk-config-section">
            <div className="bk-config-hd">
              <span className="bk-config-title">Scheme Names</span>
              <button className="bk-add-chip-btn" onClick={addSN}><Plus size={12} /> Add</button>
            </div>
            <p className="bk-config-hint">These appear as the <strong>Scheme</strong> dropdown when adding rows.</p>
            <div className="bk-chips-list">
              {schemeNames.map((s, i) => (
                <div className="bk-chip-row" key={i}>
                  <input
                    className="bk-input bk-input-sm"
                    value={s}
                    placeholder={`e.g. HDFC, HDFC_Preapproved`}
                    onChange={(e) => updateSN(i, e.target.value)}
                  />
                  <button className="bk-icon-btn bk-del-chip" onClick={() => removeSN(i)}><X size={13} /></button>
                </div>
              ))}
              {schemeNames.length === 0 && (
                <button className="bk-dashed-add" onClick={addSN}><Plus size={12} /> Add scheme name</button>
              )}
            </div>
          </div>

          {/* Customer Types */}
          <div className="bk-config-section">
            <div className="bk-config-hd">
              <span className="bk-config-title"><Users size={13} /> Customer Types</span>
              <button className="bk-add-chip-btn" onClick={addCT}><Plus size={12} /> Add</button>
            </div>
            <p className="bk-config-hint">These appear as the <strong>Customer Type</strong> dropdown when adding rows.</p>
            <div className="bk-chips-list">
              {customerTypes.map((ct, i) => (
                <div className="bk-chip-row" key={i}>
                  <input
                    className="bk-input bk-input-sm"
                    value={ct}
                    placeholder={`e.g. HDFC customers`}
                    onChange={(e) => updateCT(i, e.target.value)}
                  />
                  <button className="bk-icon-btn bk-del-chip" onClick={() => removeCT(i)}><X size={13} /></button>
                </div>
              ))}
              {customerTypes.length === 0 && (
                <button className="bk-dashed-add" onClick={addCT}><Plus size={12} /> Add customer type</button>
              )}
            </div>
          </div>

          {/* Finance Combinations */}

<div className="bk-config-section">

    <div className="bk-config-hd">

        <span className="bk-config-title">
            Finance Combinations
        </span>

        <button
            className="bk-add-chip-btn"
            onClick={addCombination}
        >
            <Plus size={12}/>
            Add
        </button>

    </div>

    <p className="bk-config-hint">
        Configure finance values for each Scheme + Customer Type.
    </p>

      <div className="bk-combinations-list">
  {combinations.map((item) => (
    <div key={item.id} className="bk-combination-card">

      <div className="bk-form-grid bk-grid-3">

        <div className="bk-field">
          <label className="bk-lbl">Scheme Name</label>

          <Sel
            value={item.schemeName}
            onChange={(v) =>
              updateCombination(item.id, "schemeName", v)
            }
            options={schemeNames.filter(Boolean)}
            placeholder="Select Scheme"
          />
        </div>

        <div className="bk-field">
          <label className="bk-lbl">Customer Type</label>

          <Sel
            value={item.customerType}
            onChange={(v) =>
              updateCombination(item.id, "customerType", v)
            }
            options={customerTypes.filter(Boolean)}
            placeholder="Select Customer"
          />
        </div>

        <div className="bk-field">
          <label className="bk-lbl">Interest %</label>

          <input
            className="bk-input bk-input-sm"
            value={item.interestPct}
            onChange={(e) =>
              updateCombination(
                item.id,
                "interestPct",
                e.target.value
              )
            }
          />
        </div>

        <div className="bk-field">
          <label className="bk-lbl">Loan %</label>

          <input
            className="bk-input bk-input-sm"
            value={item.loanPct}
            onChange={(e) =>
              updateCombination(
                item.id,
                "loanPct",
                e.target.value
              )
            }
          />
        </div>

        <div className="bk-field">
          <label className="bk-lbl">PF %</label>

          <input
            className="bk-input bk-input-sm"
            value={item.pfPct}
            onChange={(e) =>
              updateCombination(
                item.id,
                "pfPct",
                e.target.value
              )
            }
          />
        </div>

       {name.trim().toLowerCase() !== "hdfc bank" && (
  <div className="bk-field">
    <label className="bk-lbl">Flat Rate %</label>
    <input
      className="bk-input bk-input-sm"
      value={item.flatRatePct}
      onChange={(e) =>
        updateCombination(
          item.id,
          "flatRatePct",
          e.target.value
        )
      }
    />
  </div>
)}
       

        <div className="bk-field">
          <label className="bk-lbl">GST %</label>

          <input
            className="bk-input bk-input-sm"
            value={item.gstPct}
            onChange={(e) =>
              updateCombination(
                item.id,
                "gstPct",
                e.target.value
              )
            }
          />
        </div>

       {name.toLowerCase().includes("hdfc") && (
  <div className="bk-field">
    <label className="bk-lbl">PDD Charges</label>
    <input
      className="bk-input bk-input-sm"
      value={item.pddCharges}
      onChange={(e) =>
        updateCombination(
          item.id,
          "pddCharges",
          e.target.value
        )
      }
    />
  </div>
)}

        {!name.toLowerCase().includes("hdfc") && (
  <div className="bk-field">
    <label className="bk-lbl">Doc Charges</label>
    <input
      className="bk-input bk-input-sm"
      value={item.docCharges}
      onChange={(e) =>
        updateCombination(
          item.id,
          "docCharges",
          e.target.value
        )
      }
    />
  </div>
)}

{name.toLowerCase().includes("hdfc") && (
  <div className="bk-field">
    <label className="bk-lbl">Doc Charges</label>
    <input
      className="bk-input bk-input-sm"
      value="Calculated automatically"
      disabled
    />
    <small className="bk-config-hint">
      Doc Charges = (1.75% x Loan Amount) + PDD Charges
    </small>
  </div>
)}

      </div>

      <div className="bk-combination-actions">
        <button
          className="bk-icon-btn bk-del-chip"
          onClick={() => removeCombination(item.id)}
        >
          <Trash2 size={14} />
        </button>
      </div>

    </div>
  ))}
</div>



</div>


         {/*offers*/}
         <div className="bk-config-section">
  <div className="bk-config-hd">
    <span className="bk-config-title">
      Offers
    </span>

    <button
      className="bk-add-chip-btn"
      onClick={addOffer}
    >
      <Plus size={12} />
      Add
    </button>
  </div>

  <p className="bk-config-hint">
    Promotional offers shown on the website.
  </p>

  <div className="bk-chips-list">
    {offers.map((offer, i) => (
      <div
        className="bk-chip-row"
        key={i}
      >
        <input
          className="bk-input bk-input-sm"
          value={offer}
          placeholder="e.g. Zero processing fee"
          onChange={(e) =>
            updateOffer(i, e.target.value)
          }
        />

        <button
          className="bk-icon-btn bk-del-chip"
          onClick={() => removeOffer(i)}
        >
          <X size={13} />
        </button>
      </div>
    ))}

    {offers.length === 0 && (
      <button
        className="bk-dashed-add"
        onClick={addOffer}
      >
        <Plus size={12} />
        Add offer
      </button>
    )}
  </div>
</div>


   {/* CIBIL Score Categories */}
<div className="bk-config-section">
  <div className="bk-config-hd">
    <span className="bk-config-title">CIBIL Score Categories</span>
  </div>
  <p className="bk-config-hint">
    Select which credit score categories this bank accepts.
  </p>
  <div className="bk-cibil-checks">
    {CIBIL_CATEGORIES.map((cat) => {
      const active = cibilCategories.includes(cat.value);
      return (
        <button
          key={cat.value}
          type="button"
          className={`bk-cibil-toggle bk-cibil-${cat.value}${active ? " active" : ""}`}
          onClick={() => toggleCibil(cat.value)}
        >
          <span className="bk-cibil-dot" />
          <span className="bk-cibil-label">{cat.label}</span>
          <span className="bk-cibil-range">{cat.range}</span>
          {active && <span className="bk-cibil-check">✓</span>}
        </button>
      );
    })}
  </div>
</div>

        </div>

        <div className="bk-modal-ft">
          <button className="bk-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="bk-btn-primary" onClick={save} disabled={saving}>
            <Check size={14} /> {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Bank"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inp({
  field,
  form,
  errors,
  setField,
  placeholder,
  type = "text",
}) {
  return (
    <input
      className={`bk-input bk-input-sm${errors[field] ? " err" : ""}`}
      type={type}
      value={form[field] ?? ""}
      placeholder={placeholder}
      onChange={(e) => setField(field, e.target.value)}
    />
  );
}

// ─────────────────────────────────────────────
// Add / Edit Scheme Row Modal
// row = null (add) or existing row (edit)
// ─────────────────────────────────────────────
function SchemeModal({ bank, row, onSave, onClose, states, vehicles }) {
  const isEdit = !!row;
  const [form, setForm] = useState(row ? { ...row } : EMPTY_FORM());
  const [errors, setErrors] = useState({});

  const selectedCombination = bank.combinations?.find(
  (item) =>
    item.schemeName === form.schemeName &&
    item.customerType === form.customerType
);

console.log("selectedCombination", selectedCombination);

  const set = (field, val) => {
  setForm((f) => ({
    ...f,
    [field]: val,
  }));

  setErrors((e) => ({
    ...e,
    [field]: "",
  }));
};

   

  const REQUIRED = ["schemeName", "customerType", "stateId", "vehicleId",
                    "tenure",];
  const validate = () => {
    const e = {};
    REQUIRED.forEach((k) => { if (!String(form[k] ?? "").trim()) e[k] = true; });
    return e;
  };

  const save = () => {
    const e = validate();
      console.log("Validation Errors:", e);
  console.log("Form:", form);
    if (Object.keys(e).length) { setErrors(e); return; }
    //onSave({ ...form, id: row?.id ?? uid() });
    onSave({
  id: row?.id ?? uid(),
  bankId: bank.id,
  schemeName: form.schemeName,
  customerType: form.customerType,
  stateId: form.stateId,
  vehicleId: form.vehicleId,
  tenure: form.tenure,
});
  };

  const vehicle  = getVehicle(vehicles, form.vehicleId);
  
const vehicleStatePrice = vehicle?.VehicleStatePrices?.find(
  (vsp) => Number(vsp.state_id) === Number(form.stateId)
);

const onRoadPrice = Number(
  vehicleStatePrice?.values?.["On Road Price"] || 0
);
console.log("vehicle", vehicle);
console.log("VehicleStatePrices", vehicle?.VehicleStatePrices);
console.log("selected stateId", form.stateId);
  const stateOpts   = states.map((s) => ({ value: s.id, label: s.name }));
  const vehicleOpts = vehicles.map((v) => ({ value: v.id, label: v.model }));
  const tenureOpts  = TENURES.map((t) => ({ value: t, label: `${t} months` }));

const interestPct = Number(selectedCombination?.interestPct || 0);
   const loanPct = Number(selectedCombination?.loanPct || 0);
  //const loanAmount = (vehicle.onRoadPrice.toLocaleString("en-IN") * loanPct) / 100;
  const loanAmount = onRoadPrice * loanPct;
  //const flatRatePct = Number((interestPct / 1.8).toFixed(2));
  const flatRatePct =
  bank.name.trim().toLowerCase() === "hdfc bank"
    ? Number((interestPct / 1.8).toFixed(2))
    : Number(selectedCombination?.flatRatePct || 0);

  const isHdfc = bank.name.trim().toLowerCase() === "hdfc bank";
  const docCharges = isHdfc
  ? Math.round((loanAmount * 1.75) / 100 + Number(selectedCombination?.pddCharges || 0))
  : Number(selectedCombination?.docCharges || 0);

  const pfAmount = (loanAmount * Number(selectedCombination?.pfPct || 0)) / 100;
  const gstAmount = (pfAmount * Number(selectedCombination?.gstPct || 0)) / 100;
//   function calculatePMT(rate, nper, pv) {
//     if (rate === 0) return pv / nper;

//     return (
//         (rate * pv * Math.pow(1 + rate, nper)) /
//         (Math.pow(1 + rate, nper) - 1)
//     );
// }

const emi = Math.round(
    calculatePMT(
        interestPct / 100 / 12,
        Number(form.tenure),
        loanAmount
    )
);

useEffect(() => {
  if (!selectedCombination || !vehicle) return;

  const isHdfc = 
     bank.name.trim().toLowerCase() === "hdfc bank";

  const docCharges = isHdfc
     ? Math.round(loanAmount * 0.0175) +
     Number(selectedCombination?.pddCharges || 0)
   : Number(selectedCombination?.docCharges || 0);

  setForm((prev) => ({
    ...prev,
    interestPct,
    loanPct,
    loanAmount,
    flatRatePct,
    pf: Number(pfAmount.toFixed(2)),
    gst: Number(gstAmount.toFixed(2)),
    emi,
    docCharges,
  }));
}, [
  selectedCombination,
  vehicle,
  bank,
  loanAmount,
  pfAmount,
  gstAmount,
  interestPct,
  loanPct,
  flatRatePct,
  emi,
  form.tenure,
]);
  // text input helper
  // const Inp = ({ field, placeholder, type = "text" }) => (
  //   <input
  //     className={`bk-input bk-input-sm${errors[field] ? " err" : ""}`}
  //     type={type}
  //     value={form[field] ?? ""}
  //     placeholder={placeholder}
  //     onChange={(e) => set(field, e.target.value)}
  //   />
  // );

  return (
    <div className="bk-overlay" onClick={onClose}>
      <div className="bk-modal bk-modal-scheme" onClick={(e) => e.stopPropagation()}>

        <div className="bk-modal-hd">
          <div className="bk-modal-hd-left">
            {isEdit ? <Pencil size={15} /> : <Plus size={15} />}
            <span>{isEdit ? "Edit Scheme Row" : "Add Scheme Row"}</span>
            <span className="bk-modal-bank-tag">{bank.name}</span>
          </div>
          <button className="bk-icon-btn" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="bk-modal-body">

          {/* ── Row 1: Scheme · Customer Type · Tenure (DROPDOWNS) ── */}
          <div className="bk-modal-section-label">Scheme Details — use dropdowns</div>
          <div className="bk-form-grid bk-grid-3">
            <div className="bk-field">
              <label className="bk-lbl">Scheme Name <span className="bk-req">*</span></label>
              <Sel
                value={form.schemeName}
                onChange={(v) => set("schemeName", v)}
                options={bank.schemeNames}
                placeholder="Select scheme…"
                error={errors.schemeName}
              />
            </div>
            <div className="bk-field">
              <label className="bk-lbl">Customer Type <span className="bk-req">*</span></label>
              <Sel
                value={form.customerType}
                onChange={(v) => set("customerType", v)}
                options={bank.customerTypes}
                placeholder="Select type…"
                error={errors.customerType}
              />
            </div>
            <div className="bk-field">
              <label className="bk-lbl">Tenure <span className="bk-req">*</span></label>
              <Sel
                value={form.tenure}
                onChange={(v) => set("tenure", Number(v))}
                options={tenureOpts}
                placeholder="Select tenure…"
                error={errors.tenure}
              />
            </div>
          </div>

    



          {/* ── Row 2: State · Vehicle · Price Band (dropdowns, but not "the three") ── */}
          <div className="bk-modal-section-label">Vehicle & Location</div>
          <div className="bk-form-grid bk-grid-3">
            <div className="bk-field">
              <label className="bk-lbl">State <span className="bk-req">*</span></label>
              <Sel
                value={form.stateId}
                onChange={(v) => set("stateId", Number(v))}
                options={stateOpts}
                placeholder="Select state…"
                error={errors.stateId}
              />
            </div>
            <div className="bk-field">
              <label className="bk-lbl">Vehicle Model <span className="bk-req">*</span></label>
              <Sel
                value={form.vehicleId}
                onChange={(v) => set("vehicleId", Number(v))}
                options={vehicleOpts}
                placeholder="Select vehicle…"
                error={errors.vehicleId}
              />
            </div>
            {/* <div className="bk-field">
              <label className="bk-lbl">Price Band</label>
              <Sel
                value={form.priceBand}
                onChange={(v) => set("priceBand", v)}
                options={PRICE_BANDS}
                placeholder="Select…"
              />
            </div> */}
          </div>

          {/* Auto on-road price */}
          {vehicle && (
            <div className="bk-autofill">
              <span>On Road Price (auto-filled from vehicle + state)</span>
              <strong>₹{onRoadPrice.toLocaleString("en-IN")}</strong>
            </div>
          )}

          {/* ── Row 3: All financial fields — plain text inputs ── */}
          <div className="bk-modal-section-label">Financial Details — enter values</div>
          <div className="bk-form-grid bk-grid-4">
            <div className="bk-field">
              <label className="bk-lbl">Interest % <span className="bk-req">*</span></label>
    
{/* <Inp
  field="interestPct"
  form={form}
  errors={errors}
  setField={set}
  placeholder="e.g. 10.35"
  type="number"
/> */}
  <input
  className="bk-input bk-input-sm"
  value={selectedCombination?.interestPct || ""}
  readOnly
/>
              {/* <Inp field="interestPct" placeholder="e.g. 10.35" type="number" /> */}
            </div>
            <div className="bk-field">
              <label className="bk-lbl">Loan % <span className="bk-req">*</span></label>
              {/* <Inp field="loanPct" placeholder="e.g. 0.9" type="number" /> */}
              {/* <Inp
  field="loanPct"
  form={form}
  errors={errors}
  setField={set}
  placeholder="e.g. 10.35"
  type="number"
/> */}
     <input
  className="bk-input bk-input-sm"
  value={selectedCombination?.loanPct || ""}
  readOnly
/>

            </div>
            <div className="bk-field">
              <label className="bk-lbl">Loan Amount ₹ <span className="bk-req">*</span></label>
            
             <input
  className="bk-input bk-input-sm"
  value={loanAmount}
  readOnly
/>
            </div>
            <div className="bk-field">
              <label className="bk-lbl">EMI ₹ <span className="bk-req">*</span></label>
             <input
  className="bk-input bk-input-sm"
  value={emi || ""}
  readOnly
/>
              
            </div>
          </div>
          <div className="bk-form-grid bk-grid-4">
            <div className="bk-field">
              <label className="bk-lbl">PF ₹</label>
              {/* <Inp field="pf" placeholder="0" type="number" /> */}
              {/* <Inp
  field="pf"
  form={form}
  errors={errors}
  setField={set}
  placeholder="e.g. 10.35"
  type="number"
/> */}

    <input
  className="bk-input bk-input-sm"
  //value={selectedCombination?.pfPct || ""}
  value={pfAmount ? pfAmount.toFixed(2) : ""}
  readOnly
/>
            </div>

            <div className="bk-field">
  <label className="bk-lbl">GST %</label>

  <input
    className="bk-input bk-input-sm"
    value={selectedCombination?.gstPct || ""}
    readOnly
  />
</div>

  <div className="bk-field">
  <label className="bk-lbl">Flat Rate %</label>

  <input
    className="bk-input bk-input-sm"
    value={flatRatePct || ""}
    readOnly
  />
</div>

 
            <div className="bk-field">
              <label className="bk-lbl">PDD Charges ₹ <span className="bk-req">*</span></label>
              {/* <Inp
  field="docCharges"
  form={form}
  errors={errors}
  setField={set}
  placeholder="e.g. 10.35"
  type="number"
/> */}
      <input
  className="bk-input bk-input-sm"
  value={selectedCombination?.pddCharges || ""}
  readOnly
/>
            </div>
            {/* <div className="bk-field">
              <label className="bk-lbl">GST ₹</label>
            
              <Inp
  field="gst"
  form={form}
  errors={errors}
  setField={set}
  placeholder="e.g. 10.35"
  type="number"
/>
            </div> */}
            {/* <div className="bk-field">
              <label className="bk-lbl bk-lbl-dp">Down Payment ₹ <span className="bk-req">*</span></label>
              <input
                className={`bk-input bk-input-sm bk-input-dp${errors.downPayment ? " err" : ""}`}
                type="number"
                value={form.downPayment ?? ""}
                placeholder="e.g. 15929"
                onChange={(e) => set("downPayment", e.target.value)}
              />
            </div> */}
          </div>

        </div>

        <div className="bk-modal-ft">
          <button className="bk-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="bk-btn-primary" onClick={save}>
            <Check size={14} /> {isEdit ? "Save Changes" : "Add Row"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Bank Section — header + read-only table
// ─────────────────────────────────────────────
function BankSection({ bank, onEditBank, onDeleteBank, onUpdateRows, states, vehicles }) {
  const [collapsed, setCollapsed]     = useState(false);
  const [schemeModal, setSchemeModal] = useState(null); // null | { row: null } | { row }
  const [deleteRowId, setDeleteRowId] = useState(null);

  const openAdd     = () => { setSchemeModal({ row: null }); setCollapsed(false); };
  const openEdit    = (row) => setSchemeModal({ row });
  const closeModal  = () => setSchemeModal(null);

  const saveRow = (saved) => {
    const exists = bank.rows.find((r) => r.id === saved.id);
    onUpdateRows(
      exists ? bank.rows.map((r) => r.id === saved.id ? saved : r)
             : [...bank.rows, saved]
    );
    closeModal();
  };

  const deleteRow = () => {
    onUpdateRows(bank.rows.filter((r) => r.id !== deleteRowId));
    setDeleteRowId(null);
  };

  

  return (
    <div className="bk-section-card">

      {/* ── Bank header bar ── */}
      <div className="bk-sec-hd">
        <button className="bk-collapse-btn" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>
        {bank.logoUrl ? (
          <img className="bk-bank-logo" src={bank.logoUrl} alt={`${bank.name} logo`} />
        ) : (
          <Landmark size={16} className="bk-bank-ico" />
        )}
        <span className="bk-bank-name">{bank.name}</span>
        <span className="bk-badge bk-badge-rows">{bank.rows.length} row{bank.rows.length !== 1 ? "s" : ""}</span>
        <span className="bk-badge bk-badge-schemes">{bank.schemeNames.length} scheme{bank.schemeNames.length !== 1 ? "s" : ""}</span>
        <span className="bk-badge bk-badge-ct"><Users size={10} /> {bank.customerTypes.length} types</span>
        <span className="bk-badge">{bank.offers?.length || 0} offers</span>
        <div className="bk-sec-actions">
          <button className="bk-act-btn bk-act-edit" onClick={onEditBank}>
            <Pencil size={12} /> Edit Bank
          </button>
          <button className="bk-act-btn bk-act-del" onClick={onDeleteBank}>
            <Trash2 size={12} /> Delete
          </button>
        </div>
        <button className="bk-btn-add-row" onClick={openAdd}>
          <Plus size={13} /> Add Scheme
        </button>
      </div>

      {/* ── Read-only table ── */}
      {!collapsed && (
        <div className="bk-tbl-wrap">
          <table className="bk-tbl">
            <thead>
              <tr>
                <th>State</th>
                <th>Model</th>
                <th className="col-scheme">Scheme</th>
                <th className="col-ct">Customer Type</th>
                <th className="col-num">Tenure</th>
               
                <th className="col-num">On Road Price</th>
                <th>Price Band</th>
                 {/* <th className="col-cibil">CIBIL</th> */}
                <th className="col-num">Interest %</th>
                <th className="col-num">Loan %</th>
                <th className="col-num">Loan Amount</th>
                <th className="col-num col-emi">EMI</th>
                <th className="col-num">PF</th>
                <th className="col-num">Doc Charges</th>
                 <th className="col-num">GST</th>
                 <th className="col-num">Flat Rate %</th>
                {/*<th className="col-num col-dp">Downpayment</th> */}
                

                <th className="col-action"></th>
              </tr>
            </thead>
            <tbody>
              {bank.rows.length === 0 && (
                <tr>
                  <td colSpan={16} className="bk-tbl-empty">
                    No rows yet —{" "}
                    <button className="bk-inline-link" onClick={openAdd}>add a scheme row</button>
                  </td>
                </tr>
              )}

              {bank.rows.map((row) => {
                const vehicle = getVehicle(vehicles, row.vehicleId);
                const state   = getState(states, row.stateId);
                const combination = bank.combinations?.find(
                  (c) =>
                     c.schemeName === row.schemeName &&
                     c.customerType === row.customerType
                 );
                 const interestPct = Number(combination?.interestPct || row.interestPct || 0);

const loanPct = Number(combination?.loanPct || row.loanPct || 0);

const loanAmount =
  vehicle ? Math.round(vehicle.onRoadPrice * loanPct) : 0;

const pfAmount =
  Math.round(loanAmount * Number(combination?.pfPct || 0) / 100);

const gstAmount =
  Math.round(pfAmount * Number(combination?.gstPct || 0) / 100);
  
  //console.log("gst amount", gstAmount);

// const flatRatePct =
//   Number((interestPct / 1.8).toFixed(2));

const flatRatePct =
  bank.name.trim().toLowerCase() === "hdfc bank"
    ? Number((interestPct / 1.8).toFixed(2))
    : Number(combination?.flatRatePct || 0);

    const isHdfc = bank.name.trim().toLowerCase() === "hdfc bank";

  const docCharges = isHdfc
  ? Math.round(loanAmount * 0.0175) +
    Number(combination?.pddCharges || 0)
  : Number(combination?.docCharges || 0);

// const docCharges = isHdfc
//   ? Math.round((loanAmount * 1.75) / 100 + Number(combination?.pddCharges || 0))
//   : Number(combination?.docCharges || row.docCharges || 0);

//    function calculatePMT(rate, nper, pv) {
//     if (rate === 0) return pv / nper;

//     return (
//         (rate * pv * Math.pow(1 + rate, nper)) /
//         (Math.pow(1 + rate, nper) - 1)
//     );
// }

const emi = Math.round(
  calculatePMT(
    interestPct / 100 / 12,
    Number(row.tenure),
    loanAmount
  )
);

  const priceBand =
  vehicle && Number(vehicle.onRoadPrice) >= 115000
    ? "> 1.15 lakh"
    : "< 1.15 lakh";
                return (
                  <tr key={row.id} className="bk-tr">
                    <td className="td-txt">{state?.name ?? "—"}</td>
                    <td className="td-txt td-model">{vehicle?.model ?? "—"}</td>
                    <td className="td-txt">
                      <span className="bk-scheme-chip">{row.schemeName || "—"}</span>
                    </td>
                    <td className="td-txt td-ct">{row.customerType || "—"}</td>
                    <td className="td-num">
                      <span className="bk-tenure-chip">{row.tenure ? `${row.tenure}m` : "—"}</span>
                    </td>
                    <td className="td-num td-onroad">{vehicle ? `₹${inr(vehicle.onRoadPrice)}` : "—"}</td>
                    <td className="td-txt">
                      <span className="bk-band-chip">{vehicle ? priceBand : "—"}</span>
                    </td>
                   
                    <td className="td-num td-interest">{row.interestPct ? `${interestPct}%` : "—"}</td>
                    <td className="td-num">{loanPct || "—"}</td>
                    <td className="td-num">₹{inr(loanAmount)}</td>
                    <td className="td-num td-emi">₹{inr(emi)}</td>
                    <td className="td-num">
                      {Number(combination?.pfPct || 0) > 0
                          ? `₹${inr(pfAmount)}`
                          : "—"}
                    </td>
                    <td className="td-num">₹{inr(docCharges)}</td>
                    <td className="td-num">₹{inr(gstAmount)}</td>
                    <td className="td-num">{flatRatePct}%</td>
                    {/* <td className="td-num td-dp">₹{inr(row.downPayment)}</td> */}
                    <td className="td-action">
                      <div className="bk-row-actions">
                        <button className="bk-row-edit-btn" title="Edit" onClick={() => openEdit(row)}>
                          <Pencil size={13} />
                        </button>
                        <button className="bk-row-del-btn" title="Delete" onClick={() => setDeleteRowId(row.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {schemeModal && (
        <SchemeModal
          bank={bank}
          row={schemeModal.row}
          onSave={saveRow}
          onClose={closeModal}
          states={states}
          vehicles={vehicles}
        />
      )}

      {/* Delete confirm */}
      {deleteRowId && (
        <Confirm
          title="Delete Row?"
          msg="This will permanently remove this scheme row."
          onOk={deleteRow}
          onCancel={() => setDeleteRowId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────
export default function Banks() {
  const [banks, setBanks]           = useState([]);
  const [states, setStates]         = useState([]);
  const [vehicles, setVehicles]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [bankModal, setBankModal]   = useState(null);
  const [deleteBankId, setDeleteId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [banksRes, statesRes, vehiclesRes] = await Promise.all([
          fetch(API_URL),
          fetch(STATES_API_URL),
          fetch(VEHICLES_API_URL),
        ]);

        const [banksData, statesData, vehiclesData] = await Promise.all([
          banksRes.json(),
          statesRes.json(),
          vehiclesRes.json(),
        ]);

        if (!banksRes.ok || !banksData.success) {
          throw new Error(banksData.message || "Failed to fetch banks");
        }

        if (!statesRes.ok || !statesData.success) {
          throw new Error(statesData.message || "Failed to fetch states");
        }

        if (!vehiclesRes.ok || !vehiclesData.success) {
          throw new Error(vehiclesData.message || "Failed to fetch vehicles");
        }

        setBanks((banksData.data || []).map(normalizeBank));
        setStates(statesData.data || []);
        setVehicles((vehiclesData.data || []).map(normalizeVehicle));
      } catch (err) {
        console.error(err);
        alert("Failed to load bank page data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const openAdd  = ()  => setBankModal({ bank: null });
  const openEdit = (b) => setBankModal({ bank: b });

  const persistBank = async (bank) => {
    const isExisting = banks.some((b) => b.id === bank.id);
    const res = await fetch(isExisting ? `${API_URL}/${bank.id}` : API_URL, {
      method: isExisting ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bank),
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
      throw new Error(data.message || "Failed to save bank");
    }

    return normalizeBank(data.data);
  };

  const saveBank = async (saved) => {
    const savedBank = await persistBank(saved);
    setBanks((bs) => {
      const exists = bs.find((b) => b.id === savedBank.id);
      return exists
        ? bs.map((b) => b.id === savedBank.id ? savedBank : b)
        : [savedBank, ...bs];
    });
    setBankModal(null);
  };

  const deleteBank = async () => {
    try {
      const res = await fetch(`${API_URL}/${deleteBankId}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to delete bank");
      }

      setBanks((bs) => bs.filter((b) => b.id !== deleteBankId));
      setDeleteId(null);
    } catch (err) {
      alert(err.message || "Failed to delete bank");
      console.error(err);
    }
  };

  const updateRows = async (bankId, rows) => {
    const bank = banks.find((b) => b.id === bankId);
    if (!bank) return;

    try {
      const savedBank = await persistBank({ ...bank, rows });
      setBanks((bs) => bs.map((b) => b.id === bankId ? savedBank : b));
    } catch (err) {
      alert(err.message || "Failed to save scheme rows");
      console.error(err);
    }
  };

  const totalRows = banks.reduce((a, b) => a + b.rows.length, 0);

  return (
    <div className="bk-page">

      <div className="bk-page-hd">
        <div className="bk-page-hd-left">
          <div className="bk-page-ico"><Landmark size={20} color="white" /></div>
          <div>
            <h1 className="bk-page-title">Banks</h1>
            <p className="bk-page-sub">{banks.length} bank{banks.length !== 1 ? "s" : ""} · {totalRows} scheme rows</p>
          </div>
        </div>
        <button className="bk-btn-primary" onClick={openAdd}>
          <Plus size={14} /> Add Bank
        </button>
      </div>

      <div className="bk-page-body">
        {loading ? (
          <div className="bk-zero">
            <Landmark size={40} />
            <p>Loading banks...</p>
          </div>
        ) : banks.length === 0 ? (
          <div className="bk-zero">
            <Landmark size={40} />
            <p>No banks yet</p>
            <button className="bk-btn-primary" onClick={openAdd}><Plus size={14} /> Add Bank</button>
          </div>
        ) : (
          banks.map((bank) => (
            <BankSection
              key={bank.id}
              bank={bank}
              onEditBank={() => openEdit(bank)}
              onDeleteBank={() => setDeleteId(bank.id)}
              onUpdateRows={(rows) => updateRows(bank.id, rows)}
              states={states}
              vehicles={vehicles}
            />
          ))
        )}
      </div>

      {bankModal && (
        <BankModal
          bank={bankModal.bank}
          onSave={saveBank}
          onClose={() => setBankModal(null)}
        />
      )}

      {deleteBankId && (
        <Confirm
          title="Delete Bank?"
          msg={`Remove "${banks.find((b) => b.id === deleteBankId)?.name}" and all its rows?`}
          onOk={deleteBank}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}


